import React, { useMemo } from "react";
import { Badge, Calendar, Tooltip } from "antd";
import dayjsTZ from "../../../../utils/dayjs";
import {
  monthBoundsInTz,
  pickerToDayKey,
} from "../../../../utils/timezone/dayBounds";
import { getCompanyTimezone } from "../../../../utils/timezone/teamTimezone";
import { colorForRequest, STATUS_COLORS } from "../utils/statusColors";

const PlanerCalendarView = ({
  month,
  leave = [],
  employees = [],
  onSelectRequest,
  onMonthChange,
}) => {
  const empBySub = useMemo(() => {
    const map = {};
    (employees || []).forEach((e) => {
      if (e.userSub) map[e.userSub] = e;
    });
    return map;
  }, [employees]);

  const byDay = useMemo(() => {
    const map = {};
    (leave || []).forEach((req) => {
      const period = req.requestPeriod || [];
      period.forEach((ms) => {
        const key = dayjsTZ(ms).format("YYYY-MM-DD");
        if (!map[key]) map[key] = [];
        map[key].push(req);
      });
    });
    return map;
  }, [leave]);

  const nameOf = (req) => {
    const emp = empBySub[req.userSub];
    return emp
      ? `${emp.employeeFirstName || ""} ${emp.employeeLastName || ""}`.trim()
      : `${req.given_name || ""} ${req.family_name || ""}`.trim();
  };

  const cellRender = (date, info) => {
    if (info?.type !== "date") return info?.originNode;
    const key =
      pickerToDayKey(date, getCompanyTimezone()) ||
      dayjsTZ(date).format("YYYY-MM-DD");
    const items = byDay[key] || [];
    if (!items.length) return null;

    const unique = [];
    const seen = new Set();
    items.forEach((r) => {
      if (!seen.has(r.requestId)) {
        seen.add(r.requestId);
        unique.push(r);
      }
    });

    return (
      <ul className="planer-cal-events">
        {unique.slice(0, 3).map((r) => (
          <li key={r.requestId}>
            <Tooltip
              title={`${nameOf(r)} · ${r.requestType} · ${r.requestStatus}`}
            >
              <button
                type="button"
                className="planer-cal-chip"
                style={{ background: colorForRequest(r) }}
                onClick={(e) => {
                  e.stopPropagation();
                  onSelectRequest?.(r);
                }}
              >
                {nameOf(r).split(" ")[0] || r.requestType}
              </button>
            </Tooltip>
          </li>
        ))}
        {unique.length > 3 ? (
          <li className="planer-cal-more">+{unique.length - 3}</li>
        ) : null}
      </ul>
    );
  };

  return (
    <div className="planer-calendar-view">
      <div className="planer-cal-legend">
        {Object.entries(STATUS_COLORS).map(([status, color]) => (
          <Badge key={status} color={color} text={status} />
        ))}
      </div>
      <Calendar
        value={dayjsTZ(month)}
        mode="month"
        fullscreen
        onPanelChange={(val) => {
          const { startMs } = monthBoundsInTz(getCompanyTimezone(), val);
          onMonthChange?.(dayjsTZ(startMs));
        }}
        onSelect={(val) => {
          const key =
            pickerToDayKey(val, getCompanyTimezone()) ||
            dayjsTZ(val).format("YYYY-MM-DD");
          const first = (byDay[key] || [])[0];
          if (first) onSelectRequest?.(first);
        }}
        cellRender={cellRender}
      />
    </div>
  );
};

export default PlanerCalendarView;
